// ===========================================
// EtatClients — Page rapports « État des comptes clients »
// ===========================================
import React, { useState, useEffect, useMemo } from 'react';
import {
    Card, Table, Select, Button, Tag, Dropdown, Empty, DatePicker, Modal,
} from 'antd';
import {
    TeamOutlined, DownloadOutlined, FilePdfOutlined, FileExcelOutlined,
    EyeOutlined, ClearOutlined, DollarOutlined, CheckCircleOutlined,
    ExclamationCircleOutlined, UserOutlined, BankOutlined,
    QuestionCircleOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { bilansService, agencesService } from '../../common/services';
import { useGlobalAgenceState, useCanSwitchAgence, useUserAgenceId, useExporterName } from '../../common/hooks';
import { resolveAgenceLabel, addExporterFooter, PDF_HEAD_STYLES } from '../../common/utils/pdfExport';
import { borderExcelTable, addExcelLogoHeader, addExcelExportInfo } from '../../common/utils/excelExport';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatMontant, formatMontantPdf } from '../../common/utils/format';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './etat-clients.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

const fmt = (v) => formatMontant(v);
const fmtNum = (v) => formatMontantPdf(v);

const getClientName = (record) => {
    if (!record) return '—';
    if (record.type === 'entreprise') return record.raisonSociale || record.nom || '—';
    return `${record.nom || ''} ${record.prenom || ''}`.trim() || '—';
};

const EtatClients = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [guideOpen, setGuideOpen] = useState(false);
    const [totaux, setTotaux] = useState({});
    const [loading, setLoading] = useState(false);
    const [agences, setAgences] = useState([]);
    const [selectedAgence, setSelectedAgence] = useGlobalAgenceState();
    const canSwitchAgence = useCanSwitchAgence();
    const userAgenceId = useUserAgenceId();
    const exporterName = useExporterName();
    const [dateRange, setDateRange] = useState(null);

    useEffect(() => {
        agencesService.getAll().catch(() => []).then((res) => {
            setAgences(Array.isArray(res) ? res : res?.data || []);
        });
    }, []);

    const fetchData = async () => {
        const params = {};
        if (selectedAgence !== 'all') params.agence = selectedAgence;
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.start = dateRange[0].format('YYYY-MM-DD');
            params.end = dateRange[1].format('YYYY-MM-DD');
        }
        setLoading(true);
        try {
            const res = await bilansService.etatClients(params);
            const result = res?.data || res || {};
            setData(result.data || []);
            setTotaux(result.totaux || {});
        } catch (error) {
            console.error('Erreur chargement état clients:', error);
            setData([]);
            setTotaux({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgence, dateRange]);

    // ─── Filtres actifs ───────────────────────────────────────────────
    const activeFiltersCount = useMemo(() => {
        let n = 0;
        if (selectedAgence !== 'all') n++;
        if (dateRange) n++;
        return n;
    }, [selectedAgence, dateRange]);

    const clearAllFilters = () => {
        setSelectedAgence('all');
        setDateRange(null);
    };

    // ─── Colonnes ─────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 130,
            filters: [
                { text: 'Particulier', value: 'particulier' },
                { text: 'Entreprise', value: 'entreprise' },
            ],
            onFilter: (value, record) => record.type === value,
            render: (t) => (
                <Tag color={t === 'entreprise' ? 'orange' : 'blue'} className="etat-client-type-tag">
                    {(t || '').toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Nom / Prénoms',
            key: 'nom',
            width: 260,
            ellipsis: true,
            sorter: (a, b) => getClientName(a).localeCompare(getClientName(b)),
            render: (_, r) => {
                const isEntreprise = r.type === 'entreprise';
                return (
                    <div className="etat-client-cell">
                        <div className={`etat-client-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                            {isEntreprise ? <BankOutlined /> : <UserOutlined />}
                        </div>
                        <span className="etat-client-name">{getClientName(r)}</span>
                    </div>
                );
            },
        },
        {
            title: 'Contrats',
            dataIndex: 'nombreContrats',
            key: 'contrats',
            width: 100,
            align: 'center',
            sorter: (a, b) => (a.nombreContrats || 0) - (b.nombreContrats || 0),
            render: (v) => (
                <span className={`etat-client-nb ${v > 0 ? 'has-contrats' : ''}`}>{v || 0}</span>
            ),
        },
        {
            title: 'Montant total prime',
            dataIndex: 'montantTotalPrime',
            key: 'prime',
            width: 170,
            align: 'right',
            sorter: (a, b) => (a.montantTotalPrime || 0) - (b.montantTotalPrime || 0),
            render: (v) => <span className="etat-client-amount">{fmt(v || 0)}</span>,
        },
        {
            title: 'Montant payé',
            dataIndex: 'montantPaye',
            key: 'paye',
            width: 160,
            align: 'right',
            sorter: (a, b) => (a.montantPaye || 0) - (b.montantPaye || 0),
            render: (v) => <span className="etat-client-amount is-positive">{fmt(v || 0)}</span>,
        },
        {
            title: 'Solde',
            dataIndex: 'solde',
            key: 'solde',
            width: 150,
            align: 'right',
            sorter: (a, b) => (a.solde || 0) - (b.solde || 0),
            defaultSortOrder: 'descend',
            render: (v) => (
                <span className={`etat-client-amount ${v > 0 ? 'is-warning' : 'is-positive'}`}>
                    {fmt(v || 0)}
                </span>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            width: 110,
            align: 'center',
            fixed: 'right',
            render: (_, r) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    className="etat-client-action-detail"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/etat-client/${r._id}`);
                    }}
                >
                    Détail
                </Button>
            ),
        },
    ];

    // ─── Helpers export (logique préservée) ───────────────────────────
    const getAgenceNom = () => resolveAgenceLabel({ selected: selectedAgence, agences, canSwitchAgence, userAgenceId, allLabel: 'TOUTES AGENCES' });

    const handleExportPDF = () => {
        const doc = new jsPDF({ orientation: 'portrait' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;
        const agenceNom = getAgenceNom();

        doc.addImage(logoCap, 'JPEG', 14, 8, 40, 16);
        doc.setFontSize(10);
        doc.text(today, pageWidth - 14, 15, { align: 'right' });

        doc.setFontSize(14);
        doc.setFont(undefined, 'bolditalic');
        doc.setTextColor(0, 0, 139);
        doc.text('ETAT DES COMPTES CLIENTS', centerX, 32, { align: 'center' });
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(`Agence : ${agenceNom}`, centerX, 40, { align: 'center' });

        // Période de filtre (si renseignée)
        let tableStartY = 46;
        if (dateRange && dateRange[0] && dateRange[1]) {
            const du = dateRange[0].format('DD/MM/YYYY');
            const au = dateRange[1].format('DD/MM/YYYY');
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            const periodeLabel = 'Période du : ';
            const periodeMid = '   au   ';
            const fullPeriode = `${periodeLabel}${du}${periodeMid}${au}`;
            const totalW = doc.getTextWidth(fullPeriode);
            const startX = centerX - totalW / 2;
            doc.setTextColor(0);
            doc.text(periodeLabel, startX, 48);
            doc.setTextColor(139, 0, 0);
            doc.text(du, startX + doc.getTextWidth(periodeLabel), 48);
            doc.setTextColor(0);
            doc.text(periodeMid, startX + doc.getTextWidth(periodeLabel + du), 48);
            doc.setTextColor(139, 0, 0);
            doc.text(au, startX + doc.getTextWidth(periodeLabel + du + periodeMid), 48);
            doc.setTextColor(0);
            doc.setFont(undefined, 'normal');
            tableStartY = 54;
        }

        // Une ligne PAR CONTRAT : nom affiché une seule fois par client,
        // puis pour chaque contrat sa prime (à payer), ce qui a été payé et le solde.
        // Montants en espace insécable → jamais coupés sur deux lignes (ex. « 5 363 020 »).
        const fmtMt = (v) => fmtNum(v).replace(/ /g, ' ');
        const headers = ['Assurés', 'N° Police', 'Compagnie', 'Prime à payer', 'Payé', 'Solde'];
        const rows = [];
        data.forEach((d) => {
            const contrats = (d.contrats && d.contrats.length)
                ? d.contrats
                : [{ numeroPolice: '—', compagnie: '', primeTTC: d.montantTotalPrime, montantPaye: d.montantPaye, solde: d.solde }];
            contrats.forEach((ct, i) => {
                const row = [];
                // Nom fusionné (rowSpan) sur toutes les lignes du client : défini
                // uniquement sur la 1re ligne, les suivantes n'ont plus cette colonne.
                if (i === 0) {
                    row.push({ content: getClientName(d), rowSpan: contrats.length, styles: { valign: 'middle' } });
                }
                row.push(
                    ct.numeroPolice || '—',
                    ct.compagnie || '—',
                    fmtMt(ct.primeTTC),
                    fmtMt(ct.montantPaye),
                    fmtMt(ct.solde),
                );
                rows.push(row);
            });
        });
        rows.push([
            { content: 'Total :', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: fmtMt(totaux.montantTotalPrime), styles: { halign: 'right', fontStyle: 'bold' } },
            { content: fmtMt(totaux.montantPaye), styles: { halign: 'right', fontStyle: 'bold' } },
            { content: fmtMt(totaux.solde), styles: { halign: 'right', fontStyle: 'bold' } },
        ]);

        autoTable(doc, {
            startY: tableStartY,
            head: [headers],
            body: rows,
            styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.3, fillColor: false },
            headStyles: { ...PDF_HEAD_STYLES, halign: 'center' },
            alternateRowStyles: { fillColor: false },
            columnStyles: {
                0: { halign: 'left' },
                1: { halign: 'center' },
                2: { halign: 'left' },
                3: { halign: 'right' },
                4: { halign: 'right' },
                5: { halign: 'right', fontStyle: 'bold' },
            },
            margin: { left: 6, right: 6, bottom: 35 },
            tableWidth: pageWidth - 12,
        });

        addExporterFooter(doc, exporterName);
        doc.save(`etat_clients_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    const handleExportExcel = async () => {
        const today = new Date().toLocaleDateString('fr-FR');
        const agenceNom = getAgenceNom();
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Etat Clients');
        const headers = ['Assurés', 'N° Police', 'Compagnie', 'Prime à payer', 'Payé', 'Solde'];
        ws.columns = [32, 20, 26, 16, 14, 14].map((w) => ({ width: w }));
        await addExcelLogoHeader(wb, ws);

        const titleRow = ws.addRow([`ETAT DES COMPTES CLIENTS - ${agenceNom}`]);
        ws.mergeCells(titleRow.number, 1, titleRow.number, headers.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        // Période de filtre (si renseignée)
        if (dateRange && dateRange[0] && dateRange[1]) {
            const du = dateRange[0].format('DD/MM/YYYY');
            const au = dateRange[1].format('DD/MM/YYYY');
            const periodeRow = ws.addRow([`Période du ${du} au ${au}`]);
            ws.mergeCells(periodeRow.number, 1, periodeRow.number, headers.length);
            periodeRow.getCell(1).font = { bold: true, italic: true, size: 11, color: { argb: '8B0000' } };
            periodeRow.getCell(1).alignment = { horizontal: 'center' };
        }

        addExcelExportInfo(ws, { colCount: headers.length, exporterName, today });
        ws.addRow([]);

        const headerRow = ws.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: '000000' }, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        // Une ligne PAR CONTRAT ; le nom de l'assuré est fusionné verticalement
        // sur toutes les lignes de ses contrats (comme le PDF).
        data.forEach((d) => {
            const contrats = (d.contrats && d.contrats.length)
                ? d.contrats
                : [{ numeroPolice: '—', compagnie: '', primeTTC: d.montantTotalPrime, montantPaye: d.montantPaye, solde: d.solde }];
            let firstRowNum = null;
            let lastRowNum = null;
            contrats.forEach((ct, i) => {
                const row = ws.addRow([
                    i === 0 ? getClientName(d) : '',
                    ct.numeroPolice || '—',
                    ct.compagnie || '—',
                    ct.primeTTC,
                    ct.montantPaye,
                    ct.solde,
                ]);
                if (firstRowNum === null) firstRowNum = row.number;
                lastRowNum = row.number;
                row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    if (colNumber === 1) cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                    else if (colNumber === 2) cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    else if (colNumber === 3) cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                    else if (colNumber <= 6) {
                        cell.numFmt = '#,##0';
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    }
                    if (colNumber === 6 && ct.solde > 0) cell.font = { bold: true, color: { argb: 'C8102E' } };
                });
            });
            if (lastRowNum > firstRowNum) {
                ws.mergeCells(firstRowNum, 1, lastRowNum, 1);
                ws.getCell(firstRowNum, 1).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            }
        });

        const totalRow = ws.addRow(['', '', 'Total :', totaux.montantTotalPrime, totaux.montantPaye, totaux.solde]);
        totalRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            cell.font = { bold: true };
            if (colNumber === 3) cell.alignment = { horizontal: 'right', vertical: 'middle' };
            if (colNumber >= 4) {
                cell.numFmt = '#,##0';
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
            }
        });

        borderExcelTable(ws, headerRow.number, headers.length);
        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `etat_clients_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    const exportMenuItems = [
        { key: 'pdf',   label: 'Exporter en PDF',   icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#389e0d' }} />, onClick: handleExportExcel },
    ];

    return (
        <div className="etat-clients-page">
            {/* Header */}
            <header className="etat-clients-header">
                <div className="etat-clients-header-text">
                    <h1 className="etat-clients-title">
                        <TeamOutlined /> État des comptes clients
                    </h1>
                    <p className="etat-clients-subtitle">Vue d'ensemble des soldes par client</p>
                </div>
                <div className="etat-clients-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        Guide
                    </Button>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — État des comptes clients</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={620}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Ce rapport donne, pour chaque client, le total facturé, ce qui a été payé et
                    le <strong>solde restant dû</strong>.
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <TeamOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Indicateurs &amp; filtres</strong> — Totaux primes,
                                    payé et solde du portefeuille. Filtrez par <strong>agence</strong>
                                    et <strong>période</strong> ; « Effacer » réinitialise.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <DollarOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Lire les soldes</strong> — Par client : montant total
                                    prime, montant payé et <strong>solde</strong>. Un solde affiché en
                                    rouge signale un montant encore dû.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <EyeOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Détail d'un client</strong> — Ouvrez la fiche d'un
                                    client pour voir le détail de ses contrats et de ses versements.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <DownloadOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Exporter</strong> — Le bouton « Exporter » génère un
                                    PDF ou un Excel de la liste filtrée pour vos rapprochements.
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs */}
            <section className="etat-clients-kpis" aria-label="Indicateurs état clients">
                <MiniKpiCard
                    icon={<TeamOutlined />}
                    label="Clients"
                    value={data.length}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<DollarOutlined />}
                    label="Total primes"
                    value={fmt(totaux.montantTotalPrime || 0)}
                    tone="indigo"
                />
                <MiniKpiCard
                    icon={<CheckCircleOutlined />}
                    label="Total payé"
                    value={fmt(totaux.montantPaye || 0)}
                    tone="green"
                />
                <MiniKpiCard
                    icon={<ExclamationCircleOutlined />}
                    label="Total solde"
                    value={fmt(totaux.solde || 0)}
                    tone={totaux.solde > 0 ? 'red' : 'green'}
                />
            </section>

            {/* Carte principale */}
            <Card className="etat-clients-main-card">
                <div className="etat-clients-toolbar">
                    {canSwitchAgence && (
                        <Select
                            value={selectedAgence}
                            onChange={setSelectedAgence}
                            className="etat-clients-filter"
                            aria-label="Filtrer par agence"
                        >
                            <Option value="all">Toutes les agences</Option>
                            {agences.map((a) => <Option key={a._id} value={a._id}>{a.nom}</Option>)}
                        </Select>
                    )}
                    <RangePicker
                        format="DD/MM/YYYY"
                        onChange={setDateRange}
                        value={dateRange}
                        className="etat-clients-filter-dates"
                        placeholder={['Date début', 'Date fin']}
                        allowClear
                    />
                    {activeFiltersCount > 0 && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="etat-clients-btn-clear"
                        >
                            Effacer
                        </Button>
                    )}
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} className="etat-clients-btn-export">
                            Exporter
                        </Button>
                    </Dropdown>
                </div>

                <Table
                    dataSource={data}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucun client avec contrat" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    onRow={(record) => ({
                        style: { cursor: 'pointer' },
                        onClick: () => navigate(`/dashboard/etat-client/${record._id}`),
                    })}
                    rowClassName={(record) => record.solde > 0 ? 'row-solde-impaye' : ''}
                    pagination={{
                        pageSize: 25,
                        showTotal: (t) => `${t} client${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                    }}
                    summary={() => data.length > 0 ? (
                        <Table.Summary>
                            <Table.Summary.Row className="etat-clients-summary-row">
                                <Table.Summary.Cell index={0} colSpan={2}><strong>TOTAL</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="center">
                                    <strong>{data.reduce((s, r) => s + (r.nombreContrats || 0), 0)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="right">
                                    <strong className="etat-client-amount">{fmt(totaux.montantTotalPrime || 0)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4} align="right">
                                    <strong className="etat-client-amount is-positive">{fmt(totaux.montantPaye || 0)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5} align="right">
                                    <strong className={`etat-client-amount ${totaux.solde > 0 ? 'is-warning' : 'is-positive'}`}>
                                        {fmt(totaux.solde || 0)}
                                    </strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6} />
                            </Table.Summary.Row>
                        </Table.Summary>
                    ) : null}
                />
            </Card>
        </div>
    );
};

export default EtatClients;
